// БЫСТРЫЙ ЗАПУСК — ПОДСТАНОВКИ В ЗАГОТОВКИ (ТЗ QUICK-START §3.0). Одна функция `renderQuickText` на сервер, веб и
// приложение; своих вариантов правил подстановки нет.
//
// INVARIANT(scriptera): правила — ровно §3.0: `{gift}`/`{product}` с названием в кавычках и без, необязательный отрезок
// `[[ … ]]` выбрасывается целиком (с пробелом перед ним, КРОМЕ пробела после знака `:` `,` `;` `—` — он остаётся:
// «Распространённое мнение: нужны годы…», §8.25 п.2, «да» владельца 29.10), если внутри пуста хотя бы одна подстановка; затем двойные
// пробелы и «. .» схлопываются; первая буква — заглавная. Причина: шаблоны написаны только под эти формы, и при любом
// пустом поле оффера текст обязан остаться складным. Канон: ~/projects/QUICK-START-TZ.md §3.0.
import {
  QS_GIFT_WORDS,
  QS_PRODUCT_WORDS,
  type QsGiftType,
  type QsLocale,
  type QsProductType,
} from './catalog';

/** Что подставляется в заготовку. Пустая строка — «нет значения». */
export interface QsRenderCtx {
  gift: string;
  product: string;
  productName: string;
  word: string;
  offer: string;
  result: string;
  how: string;
  term: string;
  evenIf: string;
  eventDate: string;
  eventTime: string;
  /** Клиент дополняет сам: «…». */
  deadline: string;
}

/** Кавычки названия по языку. */
export function qsQuote(name: string, locale: QsLocale): string {
  const n = name.trim();
  if (!n) return '';
  return locale === 'ru' ? `«${n}»` : `“${n}”`;
}

/** Первая буква — строчная (слово «Своё» клиента в середине фразы). */
function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLocaleLowerCase() + s.slice(1) : s;
}

export function upperFirst(s: string): string {
  // Пропускаем ведущие кавычки и знаки — заглавной становится первая БУКВА.
  const m = /^([«"“„(\s]*)(\p{L})/u.exec(s);
  if (!m) return s;
  const at = m[1]!.length;
  return s.slice(0, at) + s.charAt(at).toLocaleUpperCase() + s.slice(at + 1);
}

/** Слово типа полезняшки (именительный, строчная). */
export function qsGiftWord(type: QsGiftType | string, custom: string, locale: QsLocale): string {
  if (type === 'custom') return lowerFirst(custom.trim());
  const w = QS_GIFT_WORDS[type as Exclude<QsGiftType, 'custom'>];
  return w ? w.word[locale] : '';
}

/** `{gift}`: тип + название в кавычках, если оно есть. */
export function qsGiftPhrase(type: QsGiftType | string, custom: string, name: string, locale: QsLocale): string {
  const word = qsGiftWord(type, custom, locale);
  if (sameAsType(word, name)) return word;
  const q = qsQuote(name, locale);
  return [word, q].filter(Boolean).join(' ');
}

/** Название повторяет слово типа («курс «Курс»», ведущий 29.10, снимок sale-06) — тогда только тип, без кавычек. */
function sameAsType(word: string, name: string): boolean {
  const n = name.replace(/[«»"“”„]/g, '').trim().toLocaleLowerCase();
  return n !== '' && n === word.trim().toLocaleLowerCase();
}

/** Название полезняшки для имени бота (§3.6 `{giftTitle}`): название, без него — тип с заглавной. */
export function qsGiftTitle(type: QsGiftType | string, custom: string, name: string, locale: QsLocale): string {
  const n = name.trim();
  return n || upperFirst(qsGiftWord(type, custom, locale));
}

/** Слово типа продукта. */
export function qsProductWord(type: QsProductType | string, custom: string, locale: QsLocale): string {
  if (type === 'custom') return lowerFirst(custom.trim());
  const w = QS_PRODUCT_WORDS[type as Exclude<QsProductType, 'custom'>];
  return w ? w.word[locale] : '';
}

/** `{product}`: тип + название в кавычках. */
export function qsProductPhrase(type: QsProductType | string, custom: string, name: string, locale: QsLocale): string {
  const word = qsProductWord(type, custom, locale);
  if (sameAsType(word, name)) return word;
  return [word, qsQuote(name, locale)].filter(Boolean).join(' ');
}

const PLACEHOLDER = /\{(\w+)\}/g;

function valueOf(ctx: QsRenderCtx, key: string): string | null {
  if (!(key in ctx)) return null;
  return (ctx as unknown as Record<string, string>)[key]!.trim();
}

function fill(text: string, ctx: QsRenderCtx, head = ''): string {
  return text.replace(PLACEHOLDER, (all, key: string, at: number) => {
    const v = valueOf(ctx, key);
    if (v === null) return all;
    // Подстановка в начале предложения — с заглавной («{gift} — по кнопке ниже»).
    const before = head + text.slice(0, at);
    return /(^|[.!?]\s+)$/.test(before) ? upperFirst(v) : v;
  });
}

/** Есть ли в отрезке пустая подстановка. */
function hasEmpty(text: string, ctx: QsRenderCtx): boolean {
  for (const m of text.matchAll(PLACEHOLDER)) {
    const v = valueOf(ctx, m[1]!);
    if (v !== null && v === '') return true;
  }
  return false;
}

/** Схлопнуть пробелы и знаки после выброса отрезков. */
export function qsTidy(text: string): string {
  return text
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([.,:;!?)])/g, '$1')
    .replace(/\.\s*\./g, '.')
    .replace(/…\./g, '…')
    .replace(/,\s*\./g, '.')
    .replace(/^\s+|\s+$/g, '');
}

/**
 * Заготовка → текст. Необязательные отрезки раскрываются изнутри наружу: внутренний выброшен — внешний остаётся,
 * если его собственные подстановки не пусты (`[[Результат: вы сможете {result}[[ за {term}]].]]`).
 */
export function renderQuickText(template: string, ctx: QsRenderCtx): string {
  let text = template;
  // Самый внутренний отрезок — без «[[» внутри.
  const inner = /( ?)\[\[((?:(?!\[\[)(?!\]\]).)*)\]\]/s;
  for (let guard = 0; guard < 50; guard++) {
    const m = inner.exec(text);
    if (!m) break;
    const [all, space, body] = m;
    const keep = !hasEmpty(body!, ctx);
    const before = text.slice(0, m.index);
    // §8.25 п.2: пробел после знака препинания отрезок с собой не уносит.
    const gap = !keep && space && /[:,;—]$/.test(before) ? space : '';
    text = before + (keep ? space + fill(body!, ctx, before + space) : gap) + text.slice(m.index + all.length);
  }
  return upperFirst(qsTidy(fill(text, ctx)));
}

/** Нет ли в тексте пустых плейсхолдеров и остатков разметки (замок тестов). */
export function qsHasLeftovers(text: string): boolean {
  return /\{\w+\}|\[\[|\]\]/.test(text);
}
